import { useEffect, useState } from 'react';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

const UNIT_TYPES = ['CITY_OFFICE', 'AGENCY', 'STAND', 'HUB', 'RELAY_POINT'];
const initialForm = { name: '', code: '', type: 'AGENCY', country_code: '', city_code: '', address: '' };

// Additive LOT 2 administration surface. Relay points remain managed through
// their legacy screen until the dual-write migration is validated in production.
const OperationalUnitsManagement = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/operational-units`);
      setItems(response.data?.items || []);
    } catch (error) {
      toast.error('Impossible de charger les unités opérationnelles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/operational-units`, form);
      toast.success('Unité opérationnelle créée.');
      setForm(initialForm);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Création impossible. Vérifiez le pays et la ville.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="operational-units-management">
      <Card>
        <CardHeader>
          <CardTitle>Unités opérationnelles</CardTitle>
          <CardDescription>Agences, stands, hubs, bureaux de ville et points relais. Les points relais existants restent synchronisés avec leur fiche historique.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4 md:grid-cols-2" onSubmit={create}>
            <div><Label htmlFor="unit-name">Nom</Label><Input id="unit-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label htmlFor="unit-code">Code</Label><Input id="unit-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
            <div><Label htmlFor="unit-type">Type</Label><select id="unit-type" className="flex h-10 w-full rounded-md border border-input bg-background px-3" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{UNIT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}</select></div>
            <div><Label htmlFor="unit-country">Pays ISO</Label><Input id="unit-country" required maxLength={2} value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })} placeholder="CG" /></div>
            <div><Label htmlFor="unit-city">Code ville</Label><Input id="unit-city" required value={form.city_code} onChange={(e) => setForm({ ...form, city_code: e.target.value.toUpperCase() })} placeholder="BZV" /></div>
            <div><Label htmlFor="unit-address">Adresse</Label><Input id="unit-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="md:col-span-2"><Button type="submit" disabled={saving}>{saving ? 'Création…' : 'Créer l’unité'}</Button></div>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Unités enregistrées</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p>Chargement…</p> : items.length === 0 ? <p>Aucune unité opérationnelle.</p> : (
            <div className="space-y-3">{items.map((unit) => <div key={unit.id} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3"><div><p className="font-medium">{unit.name}</p><p className="text-sm text-muted-foreground">{unit.code} · {unit.country_code} / {unit.city_code}</p></div><div className="flex gap-2"><Badge variant="secondary">{unit.type}</Badge><Badge variant={unit.status === 'active' ? 'default' : 'outline'}>{unit.status}</Badge></div></div>)}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OperationalUnitsManagement;
